import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface FrequencyRule {
  id: string;
  card_category: string;
  max_count: number;
  per_period: string;
  updated_at?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  onboarding:    "Onboarding",
  birthday:      "Birthday & anniversaries",
  milestone:     "Milestone",
  challenge:     "Challenge",
  upsell:        "Upsell",
  mori_message:  "Mori message",
  informational: "Tips & motivation",
};

const PERIOD_OPTIONS = [
  { value: "session", label: "per session" },
  { value: "day",     label: "per day" },
  { value: "week",    label: "per week" },
  { value: "month",   label: "per month" },
  { value: "ever",    label: "ever (all time)" },
] as const;

// Canonical order for display
const CATEGORY_ORDER = [
  "onboarding", "birthday", "milestone", "challenge",
  "upsell", "mori_message", "informational",
];

export default function CardFrequency() {
  const [rules, setRules] = useState<FrequencyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ max_count: number; per_period: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    setLoading(true);
    const { data, error } = await supabase
      .from("card_frequency_rules")
      .select("id, card_category, max_count, per_period, updated_at")
      .order("card_category");

    if (error) {
      toast.error("Failed to load frequency rules");
      setLoading(false);
      return;
    }

    // Sort by canonical category order
    const sorted = (data ?? []).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.card_category);
      const bi = CATEGORY_ORDER.indexOf(b.card_category);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    setRules(sorted as FrequencyRule[]);
    setLoading(false);
  }

  function startEdit(rule: FrequencyRule) {
    setEditingId(rule.id);
    setEditDraft({ max_count: rule.max_count, per_period: rule.per_period });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(null);
  }

  async function saveEdit(rule: FrequencyRule) {
    if (!editDraft) return;
    setSaving(true);
    const { error } = await supabase
      .from("card_frequency_rules")
      .update({
        max_count: editDraft.max_count,
        per_period: editDraft.per_period,
        updated_at: new Date().toISOString(),
      })
      .eq("id", rule.id);

    if (error) {
      toast.error(`Failed to save: ${error.message}`);
    } else {
      toast.success(`${CATEGORY_LABELS[rule.card_category] ?? rule.card_category} updated`);
      setRules((prev) =>
        prev.map((r) =>
          r.id === rule.id
            ? { ...r, max_count: editDraft.max_count, per_period: editDraft.per_period }
            : r
        )
      );
      setEditingId(null);
      setEditDraft(null);
    }
    setSaving(false);
  }

  function periodLabel(p: string): string {
    return PERIOD_OPTIONS.find((o) => o.value === p)?.label ?? p;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Card frequency rules</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Controls how often each category of card can appear in a user's feed.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Max per period</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => {
              const isEditing = editingId === rule.id;
              return (
                <tr key={rule.id}>
                  <td className="font-medium text-gray-900 text-sm">
                    {CATEGORY_LABELS[rule.card_category] ?? rule.card_category}
                  </td>
                  <td>
                    {isEditing && editDraft ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={99}
                          value={editDraft.max_count}
                          onChange={(e) =>
                            setEditDraft((d) =>
                              d ? { ...d, max_count: Math.max(1, Math.min(99, parseInt(e.target.value) || 1)) } : d
                            )
                          }
                          className="w-16 rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                        />
                        <select
                          value={editDraft.per_period}
                          onChange={(e) =>
                            setEditDraft((d) => d ? { ...d, per_period: e.target.value } : d)
                          }
                          className="rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                        >
                          {PERIOD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveEdit(rule)}
                          disabled={saving}
                          className="rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                        >
                          {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-700">
                        <span className="font-semibold">{rule.max_count}</span>{" "}
                        <span className="text-gray-500">{periodLabel(rule.per_period)}</span>
                      </span>
                    )}
                  </td>
                  <td>
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(rule)}
                        className="text-xs text-orange-600 hover:underline"
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rules.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-gray-400 py-8">
                  No frequency rules found.
                  Run the card engine migration to seed defaults.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-gray-400">
        Every category must always have a rule — rows cannot be deleted here.
        Changes take effect immediately for new feed sessions.
      </p>
    </div>
  );
}
